/**
 * Serwis API modułu Reklamacji (do dostawcy / kontrahenta)
 *
 * Warstwa dostępu do endpointów backendu (backend/api/reklamacje.py, prefix /api).
 * Używa wspólnej instancji axios `api` (services/api.js): baseURL zawiera już /api,
 * a interceptor automatycznie dokłada location_id (localStorage), CSRF token oraz
 * device token. Dlatego ścieżki podajemy BEZ wiodącego /api (np. 'reklamacje').
 *
 * Konwencja zwrotu spójna z resztą serwisów: zwracamy `response.data`
 * (koperta backendu: { success, data, message, status_code }), a w catch
 * rzucamy new Error(error.response?.data?.message || '...').
 */

import api from './api';
import { API_URL } from '../config/environment';

const komunikatBledu = (error, fallback) =>
  new Error(
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );

export const reklamacjeService = {
  // === LISTA / SZCZEGÓŁY ===

  /**
   * GET /api/reklamacje — lista reklamacji zalogowanego usera (nie-admin: jego
   * lokalizacje; admin: wszystkie).
   * @param {Object} params filtry: status, grupa(biezace|zakonczone), location_id,
   *   zglaszajacy, kontrahent_id, rozliczona(0|1), data_od, data_do, search, scope('all'),
   *   page, per_page
   */
  getAll: async (params = {}) => {
    try {
      const response = await api.get('reklamacje', { params });
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd pobierania listy reklamacji');
    }
  },

  /** GET /api/reklamacje/<id> — nagłówek + pozycje + załączniki (metadane). */
  get: async (id) => {
    try {
      const response = await api.get(`reklamacje/${id}`);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd pobierania reklamacji');
    }
  },

  // === CRUD ===

  /**
   * POST /api/reklamacje — utwórz reklamację (nagłówek + pozycje w jednej transakcji).
   * @param {Object} data { kontrahent_typ, kontrahent_id?, kontrahent_nazwa, ...,
   *   osoba_imie_nazwisko, osoba_email, przesylka_karton/ingerencja/protokol,
   *   pozycje: [{ produkt_id?, produkt_nazwa, ilosc, faktura_zakupu_id?, ... }] }
   * @returns { data: { id, numer_reklamacji } }
   */
  create: async (data) => {
    try {
      const response = await api.post('reklamacje', data);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd tworzenia reklamacji');
    }
  },

  /**
   * PUT /api/reklamacje/<id> — edytuj nagłówek i/lub pozycje (jeśli podano `pozycje`,
   * backend nadpisuje komplet pozycji).
   */
  update: async (id, data) => {
    try {
      const response = await api.put(`reklamacje/${id}`, data);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd aktualizacji reklamacji');
    }
  },

  /**
   * PATCH /api/reklamacje/<id>/status — rozpatrzenie.
   * @param {Object} data { status(nowa|w_trakcie|uwzgledniona|odrzucona),
   *   rozliczona?(0|1), numer_korekty_zewn? }
   */
  setStatus: async (id, data) => {
    try {
      const response = await api.patch(`reklamacje/${id}/status`, data);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd zmiany statusu reklamacji');
    }
  },

  /**
   * DELETE /api/reklamacje/<id> — miękkie usunięcie (aktywny=0).
   * @param {boolean} force twarde usunięcie (tylko admin) -> ?force=1
   */
  remove: async (id, force = false) => {
    try {
      const response = await api.delete(`reklamacje/${id}`, {
        params: force ? { force: 1 } : {}
      });
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd usuwania reklamacji');
    }
  },

  // === WYSZUKIWARKI DO FORMULARZA ===

  /**
   * GET /api/reklamacje/faktury-dla-produktu?produkt_id=... — wszystkie faktury
   * zakupu zawierające produkt (data malejąco) + oznaczenie ostatniej (auto-link).
   * @returns { data: { faktury: [...], ostatnia_id } }
   */
  fakturyDlaProduktu: async (produktId) => {
    try {
      const response = await api.get('reklamacje/faktury-dla-produktu', {
        params: { produkt_id: produktId }
      });
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd pobierania faktur dla produktu');
    }
  },

  /**
   * GET /api/reklamacje/produkty/search — wyszukiwarka towaru BEZ filtra stanu
   * (można reklamować produkt spoza stanu).
   */
  searchProdukty: async (query, limit = 20) => {
    try {
      const response = await api.get('reklamacje/produkty/search', {
        params: { query, limit }
      });
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd wyszukiwania produktów');
    }
  },

  /**
   * GET /api/reklamacje/kontrahenci/search — wyszukiwarka kontrahenta w JEDNEJ
   * kartotece (pos_klienci). Backend zwraca ksztalt zgodny z dotychczasowym:
   * { kontrahenci: [{ id, nazwa, nip, adres_ulica, adres_kod, adres_miasto, email,
   *   jest_dostawca?, jest_odbiorca? }] }.
   *
   * Wynik sluzy WYLACZNIE do zbudowania snapshotu w naglowku reklamacji - raz
   * przepisane dane nie moga byc pozniej odswiezane joinem do kartoteki.
   *
   * @param {string} query fraza (nazwa / NIP / numer klienta)
   * @param {number} limit maks. liczba podpowiedzi
   * @param {string|null} typ opcjonalne zawezenie roli: 'dostawca' | 'klient' | 'wszyscy'.
   *   Pominiecie = domyslnie wszyscy (dostawcy na gorze listy) - tak wola formularz reklamacji.
   */
  searchKontrahenci: async (query, limit = 20, typ = null) => {
    try {
      const params = { query, limit };
      if (typ) params.typ = typ;
      const response = await api.get('reklamacje/kontrahenci/search', { params });
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd wyszukiwania kontrahentów');
    }
  },

  // === ZAŁĄCZNIKI (zdjęcia, także HEIC) ===

  /**
   * POST /api/reklamacje/<id>/zalaczniki — upload jednego pliku (multipart).
   * Backend konwertuje HEIC->JPEG, kompresuje i zapisuje metadane.
   * @param {number} id reklamacja
   * @param {File} file jeden plik
   * @param {number|null} pozycjaId opcjonalne przypisanie do pozycji
   * @param {Function} onUploadProgress opcjonalny callback postępu (axios)
   * @returns { data: { id, plik_nazwa, url, mime_type, szerokosc, wysokosc, oryginal_heic } }
   */
  uploadZalacznik: async (id, file, pozycjaId = null, onUploadProgress = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (pozycjaId !== null && pozycjaId !== undefined) {
        formData.append('pozycja_id', pozycjaId);
      }
      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      };
      if (onUploadProgress) config.onUploadProgress = onUploadProgress;
      const response = await api.post(`reklamacje/${id}/zalaczniki`, formData, config);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd wysyłania załącznika');
    }
  },

  /** DELETE /api/reklamacje/<id>/zalaczniki/<zid> — usuń załącznik (rekord + plik). */
  deleteZalacznik: async (id, zid) => {
    try {
      const response = await api.delete(`reklamacje/${id}/zalaczniki/${zid}`);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd usuwania załącznika');
    }
  },

  /**
   * Bezpośredni URL do pliku załącznika (GET /api/reklamacje/<id>/zalaczniki/<zid>).
   * Uwaga: endpoint jest za autoryzacją sesyjną (cookie). Przydatny jako `src` w <img>,
   * ale gdy potrzebny pełny łańcuch nagłówków (device token) — użyj getZalacznikBlobUrl.
   * @param {boolean} thumb zwróć miniaturę (?thumb=1)
   */
  getZalacznikUrl: (id, zid, thumb = false) => {
    const base = `${API_URL}/reklamacje/${id}/zalaczniki/${zid}`;
    return thumb ? `${base}?thumb=1` : base;
  },

  /**
   * Pobranie załącznika przez autoryzowany request (blob) i zbudowanie objectURL.
   * Zwraca { url, blob } — url przekazujemy do <img src>; PAMIĘTAJ o
   * URL.revokeObjectURL(url) po odmontowaniu, by nie wyciekać pamięci.
   */
  getZalacznikBlobUrl: async (id, zid, thumb = false) => {
    try {
      const response = await api.get(`reklamacje/${id}/zalaczniki/${zid}`, {
        params: thumb ? { thumb: 1 } : {},
        responseType: 'blob'
      });
      const blob = response.data;
      return { url: URL.createObjectURL(blob), blob };
    } catch (error) {
      throw komunikatBledu(error, 'Błąd pobierania załącznika');
    }
  },

  // === PDF PROTOKOŁU ===

  /**
   * Bezpośredni URL do PDF (GET /api/reklamacje/<id>/pdf). Za autoryzacją sesyjną.
   * @param {boolean} download attachment (?download=1) zamiast inline
   */
  pdfUrl: (id, download = false) => {
    const base = `${API_URL}/reklamacje/${id}/pdf`;
    return download ? `${base}?download=1` : base;
  },

  /**
   * Pobiera PDF protokołu jako blob (responseType:'blob') — do podglądu w <iframe>
   * (wzorzec CorrectionPrintPage) lub pobrania.
   * @returns {Blob}
   */
  downloadPdf: async (id, download = false) => {
    try {
      const response = await api.get(`reklamacje/${id}/pdf`, {
        params: download ? { download: 1 } : {},
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd pobierania PDF reklamacji');
    }
  },

  // === WYSYŁKA E-MAIL ===

  /**
   * POST /api/reklamacje/<id>/email — wyślij reklamację mailem do kontrahenta
   * z konta oddziału. Na tym etapie realnie działa transport SMTP; gmail_oauth = szkielet.
   * @param {Object} data { do?, temat?, tresc? }
   */
  sendEmail: async (id, data = {}) => {
    try {
      const response = await api.post(`reklamacje/${id}/email`, data);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd wysyłki e-mail reklamacji');
    }
  },

  // === WIDOK ADMINA ===

  /**
   * GET /api/reklamacje/admin/przeglad — globalny widok admina (wszystkie oddziały),
   * pogrupowane biezace/zakonczone + statystyki.
   * @param {Object} params grupa, location_id, zglaszajacy, kontrahent_id, rozliczona,
   *   data_od, data_do, search
   */
  adminPrzeglad: async (params = {}) => {
    try {
      const response = await api.get('reklamacje/admin/przeglad', { params });
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd pobierania przeglądu reklamacji');
    }
  },

  // === KONFIGURACJA E-MAIL PER ODDZIAŁ (admin) ===

  /**
   * GET /api/reklamacje/email-config/<location_id> — odczyt konfiguracji mail oddziału
   * (sekrety zamaskowane; ma_haslo/ma_refresh_token informują czy ustawione).
   */
  getEmailConfig: async (locationId) => {
    try {
      const response = await api.get(`reklamacje/email-config/${locationId}`);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd pobierania konfiguracji e-mail');
    }
  },

  /**
   * PUT /api/reklamacje/email-config/<location_id> — zapis/utworzenie konfiguracji (upsert).
   * Puste pola sekretów (smtp_haslo/gmail_refresh_token) = bez zmiany.
   * @param {Object} data { transport, from_email, from_nazwa?, smtp_host?, smtp_port?,
   *   smtp_user?, smtp_haslo?, gmail_client_id?, gmail_refresh_token?, aktywny }
   */
  saveEmailConfig: async (locationId, data) => {
    try {
      const response = await api.put(`reklamacje/email-config/${locationId}`, data);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd zapisu konfiguracji e-mail');
    }
  },

  // === ADRESACI (staly slownik adresow e-mail) ===

  /** GET /api/reklamacje/adresaci — lista do wyboru przy wysylce. */
  getAdresaci: async (wszystkie = false) => {
    try {
      const response = await api.get(`reklamacje/adresaci${wszystkie ? '?wszystkie=1' : ''}`);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd pobierania listy adresatów');
    }
  },

  /** POST /api/reklamacje/adresaci — dodanie adresata (admin). */
  addAdresat: async (data) => {
    try {
      const response = await api.post('reklamacje/adresaci', data);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd dodawania adresata');
    }
  },

  /** PUT /api/reklamacje/adresaci/<id> — edycja adresata (admin). */
  updateAdresat: async (id, data) => {
    try {
      const response = await api.put(`reklamacje/adresaci/${id}`, data);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd zapisu adresata');
    }
  },

  /** DELETE /api/reklamacje/adresaci/<id> — usuniecie adresata (admin). */
  deleteAdresat: async (id) => {
    try {
      const response = await api.delete(`reklamacje/adresaci/${id}`);
      return response.data;
    } catch (error) {
      throw komunikatBledu(error, 'Błąd usuwania adresata');
    }
  }
};

// Aliasy zgodne z alternatywnym nazewnictwem z planu (getOne/pdfBlob/setEmailConfig/zalacznikUrl)
reklamacjeService.getOne = reklamacjeService.get;
reklamacjeService.pdfBlob = reklamacjeService.downloadPdf;
reklamacjeService.zalacznikUrl = reklamacjeService.getZalacznikUrl;
reklamacjeService.setEmailConfig = reklamacjeService.saveEmailConfig;

export default reklamacjeService;
